import { forwardRef, TextareaHTMLAttributes } from 'react';

export interface TextAreaFieldProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'> {
  label?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  rows?: number;
  resizable?: boolean;
  className?: string;
  textareaClassName?: string;
}

/**
 * TextAreaField - Multiline text input with label, error, and hint support
 */
const TextAreaField = forwardRef<HTMLTextAreaElement, TextAreaFieldProps>(
  (
    {
      label,
      error,
      hint,
      required = false,
      id,
      rows = 3,
      resizable = true,
      className = '',
      textareaClassName = '',
      ...props
    },
    ref
  ) => {
    const fieldId = id || props.name;

    const baseTextareaStyles =
      'w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-accent-500 bg-white dark:bg-dark-bg text-light-text dark:text-dark-text transition-colors';
    const errorStyles = error
      ? 'border-error focus:ring-error'
      : 'border-light-border dark:border-dark-border';
    const resizeStyles = resizable ? 'resize-y' : 'resize-none';

    return (
      <div className={`space-y-1.5 ${className}`}>
        {label && (
          <label
            htmlFor={fieldId}
            className="block text-sm font-medium text-light-text dark:text-dark-text"
          >
            {label}
            {required && <span className="text-error ml-0.5">*</span>}
          </label>
        )}
        <textarea
          ref={ref}
          id={fieldId}
          rows={rows}
          className={`${baseTextareaStyles} ${errorStyles} ${resizeStyles} ${textareaClassName}`}
          aria-invalid={!!error}
          aria-describedby={error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined}
          {...props}
        />
        {error && (
          <p id={`${fieldId}-error`} className="text-xs text-error animate-fade-in" role="alert">
            {error}
          </p>
        )}
        {hint && !error && (
          <p id={`${fieldId}-hint`} className="text-xs text-light-muted dark:text-dark-muted">
            {hint}
          </p>
        )}
      </div>
    );
  }
);

TextAreaField.displayName = 'TextAreaField';

export default TextAreaField;
